<script setup>
//#region import
import { computed, h, nextTick, ref, onMounted, onBeforeUnmount, resolveComponent, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isEmpty, merge, pick, omit, get, cloneDeep } from 'lodash-es';
import { useDetectPermission } from '@/composables/useDetectPermission.js';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useTablePaginator, useTableSort, useTableSearch } from '@/composables/useTableLS';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import updateUrl from '@/service/updateUrl';
import { useApi } from '@/api';
import eventBus from '@/setup/eventBus';
import { usePopupManager } from '@/components/PopupManager.vue';
import { usePermissions } from '@/composables/usePermissions';
import AffiliatesFilter from './components/AffiliatesFilter.vue';
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};
const LS_PATH = 'Affiliates';
const configExport = [
  {
    command: 'csv',
    label: 'CSV',
  },
  {
    command: 'xlsx',
    label: 'XLSX',
  },
];
const affiliatesStatuses = {
  approved: {
    icon: 'check',
    color: 'var(--text-positive)',
  },
  blocked: {
    icon: 'lock',
    color: 'var(--text-negative)',
  },
  rejected: {
    icon: 'times-circle',
    color: 'var(--text-body)',
  },
  pending: {
    icon: 'eye',
    color: 'var(--background-warning)',
  },
};
const FILTERS = [
  'account_status',
  'country_code',
  'with_multiaccounts',
  'referred',
  'referral_program_enabled',
  'media_buying_enabled',
  'payment_wallet',
  'payments_enabled',
  'payments_negative_carryover',
  'site_payment_method_id',
  'payments_net',
  'promo_code',
  'note_tag_id',
  'note_text',
];
const fields = [
  {
    name: 'incrementing_id',
    width: '70',
    sortable: 'custom',
    align: 'left',
  },
  {
    name: 'email',
    minWidth: '280',
    sortable: 'custom',
    link: getAffiliateLink,
    linkAppend: renderStatus,
  },
  {
    name: 'team_name',
    width: '120',
    minWidth: '120',
  },
  {
    name: 'referred_by_email',
    minWidth: '280',
    sortable: 'custom',
    link: getReferredAffiliateLink,
  },
  {
    name: 'country_code',
    minWidth: '180',
    sortable: 'custom',
    format: 'country',
  },
  {
    name: 'last_month_revenue',
    minWidth: '170',
    align: 'right',
    headerAlign: 'left',
    sortable: 'custom',
    computedClass: getCurrencyClass,
    format: 'formatMoney',
  },
  {
    name: 'available_balance',
    minWidth: '120',
    align: 'right',
    headerAlign: 'left',
    sortable: 'custom',
    computedClass: getCurrencyClass,
    format: 'formatMoney',
    currencyKey: 'balance_currency_code',
  },
  {
    name: 'hold_balance',
    minWidth: '120',
    align: 'right',
    headerAlign: 'left',
    sortable: 'custom',
    computedClass: getCurrencyClass,
    format: 'formatMoney',
    currencyKey: 'balance_currency_code',
  },
  {
    name: 'created_at',
    width: '134',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const store = useTypedStore();
const api = useApi();
const ls = useLs();
const route = useRoute();
const { checkCurrencyCode } = useFormatCurrency();
const { checkRequest, checkRoute } = useDetectPermission();

const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { hasPermission } = usePermissions();
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
const { searchQuery } = useTableSearch();
//#endregion

//#region state
const tableData = ref([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const filters = ref( {
  account_status: ['approved'],
});

const exportUrls = ref({});
const currencyCode = ref('');
const permissions = ref([]);
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const reportsSettings = computed(() => store.getters['reports/reportsSettings']);

const affiliateStatisticSupported = computed(() => {
  return !!reportsSettings.value.dimensions
    .map((dim) => dim.column_name)
    .find((grouping) => grouping === 'affiliate_email');
});
//#endregion

//#region watch
watch(currencyCode, (newValue) => {
  ls.set('Affiliates/List/currency_code', newValue);
});
//#endregion

//#region methods
function handleExportData(format) {
  window.open(exportUrls.value[format]);
}
function handleOpenTransferPopup() {
  const popupManager = usePopupManager();

  popupManager.transferAffiliates.open({
    data: {
      currency: currencyCode.value,
    },
    listeners: {
      success: () => {
        fetchData();
      },
    },
  });
}

function isViewReportsItem(row) {
  return (
    permissions.value[row.id].view_reports || adminAcl.value.is_superuser
  );
}

function changeCurrency(value) {
  currencyCode.value = value;
  fetchData();
}

function affiliateStats(row) {
  return {
    id: row.id,
    email: row.email,
    incrementing_id: row.incrementing_id,
  };
}

function getAffiliateLink(row) {
  return `/affiliates/${row.id}/info`;
}

function getReferredAffiliateLink(row) {
  return `/affiliates/${row.referred_by_id}/info`;
}

function getCurrencyClass(value) {
  return value < 0 ? 'negative' : '';
}


function renderStatus(row) {
  if (row.account_status === 'approved') {
    return '';
  }
  return h(resolveComponent('ui-icon'), {
    lib: 'fa',
    substyle: 'fas',
    size: 12,
    color: affiliatesStatuses[row.account_status].color,
    name: affiliatesStatuses[row.account_status].icon,
    class: 't:ml-4',
  });
}

const pageChange = (newPage, newSize) => {
  if (page.value !== newPage || limit.value !== newSize) {
    page.value = newPage;
    limit.value = newSize;
    fetchData();
  }
};

const handleSort = ({ prop, order }) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function _query() {
  const { referred, ...restFilters } = filters.value;
  const { referral_id, id } = referred || {};
  return {
    ...optionsSort.value,
    ...restFilters,
    affiliate_referral_id: id,
    search: searchQuery.value,
    referred_by: referral_id,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    currency_code: currencyCode.value,
  };
};

function _completedFilterUrl() {
  const _dataFilters = {
    ...optionsSort.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    currency_code: currencyCode.value,
    ...filters.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

function fetchData(action = '') {
  if (action !== 'silent' && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  const options = _query();

  api.getAffiliatesWithStats(options).then((response) => {
    count.value = response.data.misc.count;
    currencyCode.value = response.data.misc.currency_code;
    tableData.value = cloneDeep(response.data.payload);
    permissions.value = cloneDeep(response.data.misc.permissions);
    exportUrls.value = cloneDeep(response.data.misc.export_urls);
    //this.$refs.filter.filtersOpen = false;
  }).finally(() => {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  });
};

function changeLocaleGlobal() {
  fetchData('silent');
};

function initData() {
  if (checkRequest('affiliates_view')) {
    const _urlData = updateUrl.getParseParams(route.hash);

    const {
      with_multiaccounts,
      referral_program_enabled,
      payments_enabled,
      media_buying_enabled,
      payments_negative_carryover,
      account_status,
      ...restFilters
    } = pick(_urlData, FILTERS);

    filters.value = {
      ...restFilters,
      account_status: account_status || [],
      ...(with_multiaccounts && { with_multiaccounts: with_multiaccounts === 'true' }),
      ...(referral_program_enabled && { referral_program_enabled: referral_program_enabled === 'true' }),
      ...(payments_enabled && { payments_enabled: payments_enabled === 'true' }),
      ...(media_buying_enabled && { media_buying_enabled: media_buying_enabled === 'true' }),
      ...(payments_negative_carryover && { payments_negative_carryover: payments_negative_carryover === 'true' }),
    };
    if (!isEmpty(_urlData)) {
      setPage(_urlData);

      delete _urlData.page;
      delete _urlData.limit;

      merge(optionsSort.value, omit(_urlData, FILTERS));

      currencyCode.value = checkCurrencyCode(get(_urlData, ['currency_code']));
    } else {
      currencyCode.value = checkCurrencyCode(ls.get(`${LS_PATH}/currency_code`));
      filters.value = {
        account_status: ['approved'],
        ...ls.get(`${LS_PATH}/filters`),
      };
    }
    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('affiliates_view');
}

function getAffiliatesFilter(data) {
  filters.value = data;
  ls.set(`${LS_PATH}/filters`, data);

  handleRefresh();
}
//#endregion

//#region hooks
initData();

onMounted(() => {
  if (checkRequest('affiliates_view')) {
    eventBus.$on('changeLocaleGlobal', changeLocaleGlobal);
  }
});

onBeforeUnmount(() => {
  eventBus.$off('changeLocaleGlobal', changeLocaleGlobal);
});
//#endregion
</script>

<template>
  <div class="affiliates-list">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('affiliates.name') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <AffiliatesFilter
          :model-value="filters"
          @submit="getAffiliatesFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :model-value="currencyCode"
          :currencies="customerCurrencies"
          @update:model-value="changeCurrency"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="hasPermission('affiliates_sub_affiliates_edit_settings')"
          is-icon
          icon="right-left"
          @click="handleOpenTransferPopup"
        />
        <ExportData
          :data="configExport"
          @export-data="handleExportData"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="affiliates.list"
      @sort="handleSort"
    >
      <template
        v-if="hasPermission('reports_view') && affiliateStatisticSupported"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>

          <template v-slot="scope">
            <ActionIcon
              v-if="isViewReportsItem(scope.row)"
              icon="chart-bar"
              color="#3b94e3"
              :tooltip="$t('affiliates.cardView.toolbar.btns.statistics')"
              :router-to="{
                path: '/reports/custom',
                query: {
                  affiliate_stats: $qs.stringify(affiliateStats(scope.row), {
                    arrayFormat: 'repeat',
                  }),
                },
              }"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

